import { Box, Stack, useBreakpointValue } from '@chakra-ui/react'
import ImageBox from 'components/common/ImageBox'
import { H1, H6, SuperTitle } from 'components/typography'
import { useIsSmartfitClient } from 'hooks/helpers/useIsSmartfitClient'
import { HERO_SECTION_ID } from 'layouts/LadingLayout/components/Navbar/components/NavbarDesktop'
import { FC } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import { SlideCard } from './SlideCard'
import { swiperLinks, swiperLinksSmartFit } from './swiperLinks'

interface MensHealthSectionProps {
  /** GEO home (PRD 7.2): the condition shortcuts. */
  conditionLinks?: { title: string; href: string }[]
}

const MensHealthSection: FC<MensHealthSectionProps> = ({ conditionLinks }) => {
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const isSmartfit = useIsSmartfitClient()
  const links = isSmartfit ? swiperLinksSmartFit : conditionLinks ?? swiperLinks

  return (
    <Stack id={HERO_SECTION_ID} height="100vh" w="full" spacing="none" pos="relative">
      <Box height="full" width="full" pos="absolute" zIndex="-1000">
        <video
          src="video/close_up_face_smile.mov"
          muted
          autoPlay
          loop
          playsInline
          style={{ objectFit: 'cover', height: '100%', width: '100%' }}
        />
      </Box>
      <Box height="full" width="full" bg="black" opacity="0.15" pos="absolute" zIndex="-1000" />

      <Stack
        direction={{ base: 'column', lg: 'row' }}
        h={{ base: 'full', lg: 'auto' }}
        justify={{ base: 'center', lg: 'space-between' }}
        align={{ lg: 'center' }}
        mt={{ base: '96px', lg: 'auto' }}
        px={{ base: '24px', lg: '64px' }}
        pb={{ base: '24px', lg: '52px' }}
      >
        <Box maxW={{ base: '', lg: '860px' }} flex={1}>
          {!isMobile && !isSmartfit && (
            <SuperTitle fontSize="4.25rem" lineHeight="68px" color="alabaster">
              Saúde e bem-estar masculino com qualidade, praticidade e sem tabus
            </SuperTitle>
          )}

          {!isMobile && isSmartfit && (
            <SuperTitle fontSize="3.5rem" lineHeight="68px" color="alabaster">
              Saúde e bem-estar masculino com qualidade, praticidade e sem tabus
            </SuperTitle>
          )}

          {isMobile && (
            <H1 color="alabaster" display={{ base: 'block', lg: 'none' }}>
              Saúde e bem-estar masculino sem tabu
            </H1>
          )}
        </Box>
        {isSmartfit && (
          <Stack
            w="auto"
            h={{ base: 'auto', lg: 'full' }}
            maxW={{ base: 'full', lg: 'max-content' }}
            direction="row"
            justify={{ base: 'start', lg: 'end' }}
          >
            <ImageBox
              src="\landings\smart-fit-logo.png"
              alt="Logo SmartFit Nutri"
              w={{ base: '175px', lg: '350px' }}
              h={{ base: '75px', lg: '150px' }}
            />
          </Stack>
        )}
      </Stack>
      {isMobile && <Box flex={1} />}

      <H6 px={{ base: '24px', lg: '64px' }} pb={{ base: '40px', lg: '54px' }} color="alabaster">
        Recupere sua auto-estima <br /> com consultas a partir de{' '}
        {isSmartfit ? (
          <>
            <Box
              as="u"
              display="inline"
              textDecoration="line-through"
              textDecorationThickness="2px"
              textDecorationColor="dandelion"
              mr={2}
            >
              R$ 79
            </Box>
            <Box
              as="u"
              textDecorationThickness="3px"
              textUnderlineOffset="6px"
              textDecorationColor="dandelion"
              display="inline"
            >
              R$ 71
            </Box>
          </>
        ) : (
          <Box
            as="u"
            textDecorationThickness="3px"
            textUnderlineOffset="6px"
            textDecorationColor="dandelion"
            display="inline"
          >
            {' '}
            R$ 79
          </Box>
        )}
      </H6>

      <Box px={{ base: 'none', lg: '64px' }} pb={{ base: '36px', lg: '82px' }}>
        <Swiper
          grabCursor={true}
          slidesPerView={1.53}
          slidesOffsetBefore={24}
          spaceBetween={28}
          slidesOffsetAfter={24}
          breakpoints={{
            620: {
              slidesPerView: 2.53,
              spaceBetween: 32,
              slidesOffsetBefore: 24,
              slidesOffsetAfter: 24,
              pagination: false
            },
            990: {
              slidesPerView: 4,
              spaceBetween: 32,
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
              pagination: false
            }
          }}
        >
          {links.map(({ title, href }) => {
            return (
              <SwiperSlide
                key={title}
                style={{
                  height: 'auto'
                }}
              >
                <SlideCard title={title} href={href} />
              </SwiperSlide>
            )
          })}
        </Swiper>
      </Box>
    </Stack>
  )
}

export default MensHealthSection
